import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../utils/logger';
import { 
  MessageSquare, 
  Search, 
  User,
  Building,
  Target,
  Send,
  Paperclip,
  ArrowLeft,
  MoreVertical,
  CheckCheck,
  Star,
  FileText,
  CheckSquare,
  Calendar,
  Clock,
  Plus,
  Phone,
  Mail,
  Activity,
  TrendingUp,
  Edit,
  X,
  Filter,
  Archive,
  Tag,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Bot,
  Sparkles,
  ThumbsUp,
  XCircle,
  Trophy,
  Info,
  StopCircle,
  Zap
} from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { useRBACData } from '../hooks/useRBACData';
import { LEAD_STATUS } from '../config/supabase';
import GoogleCalendarService from '../services/googleCalendarService';
import { replyValidationService } from '../services/replyValidationService';
import advancedEmailAutomationService from '../services/advancedEmailAutomationService';

const Inbox = () => {
  const navigate = useNavigate();
  
  // Use RBAC-filtered data for role-based access
  const { leads: rbacLeads, leadEvents: rbacLeadEvents, messages: rbacMessages } = useRBACData();
  const { 
    sendEmailReply,
    sendMessage,
    markConversationAsRead,
    isConversationRead,
    getConversationReadTimestamp,
    fetchNotes,
    addNote,
    fetchTasks,
    addTask,
    toggleTask,
    fetchMeetings,
    scheduleMeeting,
    updateLeadStatus,
    userTimezone,
    formatDateInUserTimezone,
    formatTimeInUserTimezone
  } = useData();
  
  // Use RBAC-filtered data
  const leads = rbacLeads || [];
  const leadEvents = rbacLeadEvents || [];
  const messages = rbacMessages || [];
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('unread'); // unread, read, valid (no 'all' tab)
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [conversations, setConversations] = useState([]);
  
  // Advanced features state
  const [rightPanelView, setRightPanelView] = useState('timeline');
  const [messageType, setMessageType] = useState('message'); // 'message' or 'email'
  const [isSending, setIsSending] = useState(false);
  const [leadNotes, setLeadNotes] = useState([]);
  const [leadTasks, setLeadTasks] = useState([]);
  const [leadMeetings, setLeadMeetings] = useState([]);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [showTaskInput, setShowTaskInput] = useState(false);
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    duration: 30,
    type: 'call',
    syncToGoogle: true
  });
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [actionMenuPosition, setActionMenuPosition] = useState({ x: 0, y: 0 });
  const actionMenuRef = useRef(null);
  const [automationStatus, setAutomationStatus] = useState({ hasActiveAutomation: false, activeSequences: [] });
  const [isStoppingAutomation, setIsStoppingAutomation] = useState(false);
  
  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setShowActionMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load automation status when a conversation is selected
  useEffect(() => {
    const loadAutomationStatus = async () => {
      if (selectedConversation?.leadEmail) {
        const status = await advancedEmailAutomationService.getLeadAutomationStatus(selectedConversation.leadEmail);
        setAutomationStatus(status);
      } else {
        setAutomationStatus({ hasActiveAutomation: false, activeSequences: [] });
      }
    };
    loadAutomationStatus();
  }, [selectedConversation?.leadEmail]);

  // Handle manual stop of automation emails for a lead
  const handleStopAutomation = async () => {
    if (!selectedConversation?.leadEmail || isStoppingAutomation) return;
    
    setIsStoppingAutomation(true);
    try {
      const result = await advancedEmailAutomationService.manuallyStopLeadAutomation(selectedConversation.leadEmail);
      if (result.success) {
        logger.log(`✅ Automation stopped for ${selectedConversation.leadEmail}: ${result.stoppedSequences} sequences, ${result.cancelledExecutions} executions`);
        setAutomationStatus({ hasActiveAutomation: false, activeSequences: [] });
      } else {
        logger.error('Failed to stop automation:', result.error);
      }
      setShowActionMenu(false);
    } catch (error) {
      logger.error('Error stopping automation:', error);
    } finally {
      setIsStoppingAutomation(false);
    }
  };
  
  // Handle lead status change from action menu
  const handleLeadStatusChange = async (newStatus) => {
    if (!selectedConversation?.leadEmail) return;
    
    const lead = leads.find(l => l.email === selectedConversation.leadEmail);
    if (lead) {
      try {
        await updateLeadStatus(lead.id, newStatus);
        logger.log(`✅ Lead status updated to ${newStatus}`);
        setShowActionMenu(false);
      } catch (error) {
        logger.error('Error updating lead status:', error);
      }
    }
  };
  
  // Navigate to lead detail page using app's custom event system
  const handleNavigateToLead = (leadEmail, e) => {
    e.stopPropagation();
    const lead = leads.find(l => l.email === leadEmail);
    if (lead) {
      // Use custom event to trigger lead detail view (app uses state-based navigation, not routes)
      window.dispatchEvent(new CustomEvent('viewLead', { detail: { lead } }));
    }
  };
  
  const rightPanelViews = [
    { id: 'timeline', name: 'Timeline', icon: Clock },
    { id: 'notes', name: 'Notes', icon: FileText },
    { id: 'tasks', name: 'Tasks', icon: CheckSquare },
    { id: 'calendar', name: 'Calendar', icon: Calendar }
  ];

  const handleSelectConversation = async (conversation) => {
    // Mark this conversation as read in persistent storage immediately
    markConversationAsRead(conversation.leadEmail);
    
    // Update the conversation with read status
    const updatedConversation = {
      ...conversation,
      messages: conversation.messages.map(msg => ({ ...msg, isRead: true })),
      unreadCount: 0
    };
    
    // Update the conversations list immediately for instant UI feedback
    setConversations(prevConversations => 
      prevConversations.map(conv => 
        conv.leadEmail === conversation.leadEmail ? updatedConversation : conv
      )
    );
    
    setSelectedConversation(updatedConversation);
    
    // Load advanced features data for the selected lead
    await loadLeadAdvancedData(conversation.leadEmail);
  };
  
  // Load advanced data for selected lead (notes, tasks, meetings, timeline)
  const loadLeadAdvancedData = async (leadEmail) => {
    if (!leadEmail) return;
    
    try {
      const [notesData, tasksData, meetingsData] = await Promise.all([
        fetchNotes(leadEmail),
        fetchTasks(leadEmail),
        fetchMeetings(leadEmail)
      ]);
      
      setLeadNotes(notesData || []);
      setLeadTasks(tasksData || []);
      setLeadMeetings(meetingsData || []);
      
      // Create timeline events from all activities
      const timeline = [];
      
      // Add notes to timeline
      (notesData || []).forEach(note => {
        timeline.push({
          id: `note-${note.id}`,
          type: 'note',
          title: 'Note Added',
          description: note.content,
          timestamp: note.created_at,
          icon: FileText
        });
      });
      
      // Add tasks to timeline
      (tasksData || []).forEach(task => {
        timeline.push({
          id: `task-${task.id}`,
          type: 'task',
          title: task.completed ? 'Task Completed' : 'Task Created',
          description: task.title,
          timestamp: task.completed ? task.updated_at : task.created_at,
          icon: CheckSquare
        });
      });
      
      // Add meetings to timeline
      (meetingsData || []).forEach(meeting => {
        timeline.push({
          id: `meeting-${meeting.id}`,
          type: 'meeting',
          title: 'Meeting Scheduled',
          description: meeting.title,
          timestamp: meeting.created_at,
          icon: Calendar
        });
      });
      
      // Add messages to timeline
      const selectedConv = conversations.find(c => c.leadEmail === leadEmail);
      if (selectedConv) {
        selectedConv.messages.forEach(message => {
          timeline.push({
            id: `message-${message.id}`,
            type: 'message',
            title: message.type === 'sent' ? 'Message Sent' : 'Message Received',
            description: message.text.substring(0, 100) + (message.text.length > 100 ? '...' : ''),
            timestamp: message.timestamp,
            icon: MessageSquare
          });
        });
      }
      
      // Sort timeline by timestamp (newest first)
      timeline.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setTimelineEvents(timeline);
      
    } catch (error) {
       logger.error('Error loading lead advanced data:', error);
     }
   };
   
   // Enhanced message sending with both message and email support
   const handleSendMessage = async () => {
     if (!replyText.trim() || !selectedConversation?.leadEmail || isSending) return;
     
     setIsSending(true);
     
     try {
       if (messageType === 'email') {
         // Send email reply via n8n and Instantly
         await sendEmailReply(selectedConversation.leadEmail, replyText);
         logger.log('✓ Email reply sent successfully');
       } else {
         // Send regular internal message
         await sendMessage(selectedConversation.leadEmail, replyText, 'sent');
         logger.log('✓ Message sent successfully');
       }
       
       setReplyText('');
       
       // Refresh conversation data
       await loadLeadAdvancedData(selectedConversation.leadEmail);
       
     } catch (error) {
       logger.error('Error sending message:', error);
     } finally {
       setIsSending(false);
     }
   };
   
   // Add note function
   const handleAddNote = async () => {
     if (!newNote.trim() || !selectedConversation?.leadEmail) return;
     
     try {
       await addNote(selectedConversation.leadEmail, newNote);
       setNewNote('');
       setShowNoteInput(false);
       await loadLeadAdvancedData(selectedConversation.leadEmail);
     } catch (error) {
       logger.error('Error adding note:', error);
     }
   };
   
   // Add task function
   const handleAddTask = async () => {
     if (!newTask.trim() || !selectedConversation?.leadEmail) return;
     
     try {
       await addTask(selectedConversation.leadEmail, newTask);
       setNewTask('');
       setShowTaskInput(false);
       await loadLeadAdvancedData(selectedConversation.leadEmail);
     } catch (error) {
       logger.error('Error adding task:', error);
     }
   };
   
   // Toggle task completion
   const handleToggleTask = async (taskId) => {
     try {
       await toggleTask(taskId);
       await loadLeadAdvancedData(selectedConversation.leadEmail);
     } catch (error) {
       logger.error('Error toggling task:', error);
     }
   };
   
   // Schedule meeting function
   const handleScheduleMeeting = async () => {
     if (!meetingForm.title || !meetingForm.date || !meetingForm.time || !selectedConversation?.leadEmail) return;
     
     try {
       const lead = leads.find(l => l.email === selectedConversation.leadEmail);
       
       const meetingData = {
         title: meetingForm.title,
         description: meetingForm.description,
         meeting_date: meetingForm.date,
         meeting_time: meetingForm.time,
         duration: meetingForm.duration,
         meeting_type: meetingForm.type,
         contact_name: lead?.first_name || selectedConversation.leadEmail.split('@')[0],
         contact_email: selectedConversation.leadEmail,
         contact_phone: lead?.phone || '',
         notes: meetingForm.description
       };
       
       await scheduleMeeting(meetingData);
       
       // Sync to Google Calendar if enabled
       if (meetingForm.syncToGoogle) {
         try {
           const startDateTime = new Date(`${meetingForm.date}T${meetingForm.time}`);
           const endDateTime = new Date(startDateTime.getTime() + meetingForm.duration * 60000);
           
           await GoogleCalendarService.createEvent({
             summary: meetingForm.title,
             description: meetingForm.description,
             start: { dateTime: startDateTime.toISOString() },
             end: { dateTime: endDateTime.toISOString() },
             attendees: [{ email: selectedConversation.leadEmail }]
           });
         } catch (calendarError) {
           logger.warn('Failed to sync to Google Calendar:', calendarError);
         }
       }
       
       setMeetingForm({
         title: '',
         description: '',
         date: '',
         time: '',
         duration: 30,
         type: 'call',
         syncToGoogle: true
       });
       setShowMeetingForm(false);
       await loadLeadAdvancedData(selectedConversation.leadEmail);
       
     } catch (error) {
       logger.error('Error scheduling meeting:', error);
     }
   };

  // Process messages and conversations
  const processedConversations = useMemo(() => {
    const conversationMap = new Map();
    
    // Group messages by lead email - include both replies and sent emails
    leadEvents
      .filter(event => (event.event_type === 'reply' && event.reply_text) || (event.event_type === 'sent' && event.email_content))
      .forEach(event => {
        const leadEmail = event.lead_email;
        const lead = leads.find(l => l.email === leadEmail);
        
        if (!conversationMap.has(leadEmail)) {
          conversationMap.set(leadEmail, {
            leadEmail,
            leadName: lead?.first_name || leadEmail.split('@')[0],
            leadCompany: lead?.company || '',
            campaignName: event.campaign_name || lead?.campaign_name || 'Unknown',
            leadScore: lead?.score || 0,
            leadStatus: lead?.status || 'new',
            messages: [],
            lastMessageDate: null,
            lastMessageText: '',
            unreadCount: 0,
            hasValidReplies: false,
            validReplyCount: 0,
            autoReplyCount: 0
          });
        }
        
        // Track validation status for reply events
        if (event.event_type === 'reply') {
          const conversation = conversationMap.get(leadEmail);
          const validationStatus = event.validation_status || 'pending';
          if (validationStatus === 'valid') {
            conversation.hasValidReplies = true;
            conversation.validReplyCount = (conversation.validReplyCount || 0) + 1;
          } else if (validationStatus === 'auto_reply' || validationStatus === 'out_of_office') {
            conversation.autoReplyCount = (conversation.autoReplyCount || 0) + 1;
          }
        }
        
        const conversation = conversationMap.get(leadEmail);
        
        // Determine if this is a sent or received email
        const isSentEmail = event.event_type === 'sent';
        const emailText = isSentEmail ? event.email_content : event.reply_text;
        
        // Extract just the main message body, removing email headers and quoted text
        let messageText = emailText.trim();
        
        // Remove email headers and quoted content
        // Split by common email separators
        const lines = messageText.split('\n');
        const cleanLines = [];
        
        for (let i = 0; i < lines.length; i++) {
           const line = lines[i].trim();
           
           // Stop at common email reply indicators
           if (line.startsWith('On ') && (line.includes(' wrote:') || (line.includes(' at ') && line.includes('>')))) {
             break;
           }
           
           // Skip lines with timestamp and sender info (e.g., "2:20 AM AIMAN ALI SHEZAN < email> wrote:")
            if (line.includes(' wrote:') && (line.includes('AM ') || line.includes('PM ') || line.includes('@'))) {
              continue;
            }
            
            // Skip "On [date] at [time]" patterns
            if (/On\s+\w+,\s+\w+\s+\d+,\s+\d+\s+at\s+\d+:\d+\s*[AP]M/.test(line)) {
              continue;
            }
            
            // Skip lines with sender names and email patterns (e.g., "AIMAN ALI SHEZAN <")
            if (line.includes('<') && (line.includes('@') || /^[A-Z\s]+</.test(line))) {
              continue;
            }
            
            // Skip quoted lines that start with >
            if (line.startsWith('>')) {
              continue;
            }
            
            // Skip email headers
            if (line.includes('@') && (line.includes('<') || line.includes('From:') || line.includes('To:'))) {
              continue;
            }
            
            // Skip lines that are just sender names or email signatures
            if (/^[A-Z\s]+$/.test(line) && line.length > 10) {
              continue;
            }
           
           // Skip empty lines at the beginning
           if (cleanLines.length === 0 && line === '') {
             continue;
           }
           
           cleanLines.push(line);
         }
        
        // Join the clean lines and remove extra whitespace
         messageText = cleanLines.join(' ').trim();
         
         // Additional cleanup: remove any remaining email patterns
         // Remove "On [date] at [time] [NAME] <" patterns
         messageText = messageText.replace(/On\s+\w+,\s+\w+\s+\d+,\s+\d+\s+at\s+\d+:\d+\s*[AP]M\s+[A-Z\s]+</g, '').trim();
         // Remove standalone name patterns
         messageText = messageText.replace(/^[A-Z][A-Z\s]+$/g, '').trim();
         messageText = messageText.replace(/[A-Z\s]+<[^>]*>/g, '').trim();
         messageText = messageText.replace(/^[A-Z][A-Z\s]+$/g, '').trim();
         // Remove any remaining "On [date]" patterns
         messageText = messageText.replace(/On\s+\w+,\s+\w+\s+\d+,\s+\d+.*$/g, '').trim();
         
         // If no clean content found, take the first non-empty line
         if (!messageText) {
           const firstLine = lines.find(line => line.trim() && !line.startsWith('>') && !line.includes('<'));
           messageText = firstLine ? firstLine.trim() : event.reply_text.trim();
         }
         
         // Final cleanup - if message is still just a name pattern, skip it
         if (/^[A-Z\s]+$/.test(messageText) && messageText.length > 5) {
           messageText = '';
         }
        
        // Only add message if there's actual content
         if (messageText && messageText.length > 0) {
           // Check if this specific message is read using timestamp comparison
           const messageIsRead = isConversationRead(leadEmail, event.created_at);
           
           // Add each reply as a separate message with validation status
           conversation.messages.push({
             id: event.id,
             text: messageText,
             timestamp: event.created_at,
             type: isSentEmail ? 'sent' : 'received',
             isRead: messageIsRead,
             sender_type: isSentEmail ? 'user' : 'lead',
             email_status: isSentEmail ? (event.email_status || 'sent') : null,
             error_message: event.error_message || null,
             validation_status: event.validation_status || null
           });
           
           // Count as unread if message arrived after read timestamp (for received messages only)
           if (!isSentEmail && !messageIsRead) {
             conversation.unreadCount += 1;
           }
         }
        
        // Update last message date and text
        if (!conversation.lastMessageDate || new Date(event.created_at) > new Date(conversation.lastMessageDate)) {
          conversation.lastMessageDate = event.created_at;
          conversation.lastMessageText = messageText;
        }
      });
    
    // Also include messages from the Supabase messages table
    messages.forEach(message => {
      const leadEmail = message.lead_email;
      const lead = leads.find(l => l.email === leadEmail);
      
      if (!conversationMap.has(leadEmail)) {
        conversationMap.set(leadEmail, {
          leadEmail,
          leadName: lead?.first_name || leadEmail.split('@')[0],
          leadCompany: lead?.company || '',
          campaignName: lead?.campaign_name || 'Unknown',
          leadScore: lead?.score || 0,
          leadStatus: lead?.status || 'new',
          messages: [],
          lastMessageDate: null,
          lastMessageText: '',
          unreadCount: 0
        });
      }
      
      const conversation = conversationMap.get(leadEmail);
      
      // Add message from Supabase messages table
      conversation.messages.push({
        id: `msg_${message.id}`,
        text: message.content,
        timestamp: message.created_at,
        type: message.sender_type === 'agent' ? 'sent' : 'received',
        isRead: isConversationRead(leadEmail),
        sender_type: message.sender_type,
        email_status: message.email_status,
        error_message: message.error_message
      });
      
      // Update last message date and text
      if (!conversation.lastMessageDate || new Date(message.created_at) > new Date(conversation.lastMessageDate)) {
        conversation.lastMessageDate = message.created_at;
        conversation.lastMessageText = message.content;
      }
    });
    
    // Sort messages within each conversation by timestamp and remove duplicates
    conversationMap.forEach(conversation => {
      // Deduplicate messages within each conversation
      const uniqueMessages = [];
      const seenMessages = new Set();
      
      conversation.messages.forEach(current => {
        // Create multiple possible keys to catch duplicates with different property names
        const content = (current.text || current.content || '').trim();
        const timestamp = current.timestamp || current.created_at;
        const messageType = current.type || current.sender_type || '';
        
        // Create a normalized key
        const normalizedKey = `${content}-${timestamp}-${messageType}`;
        
        // Also check for near-duplicate timestamps (within 1 second) with same content
        const currentTime = new Date(timestamp).getTime();
        
        let isDuplicate = false;
        
        // Check exact match first
        if (seenMessages.has(normalizedKey)) {
          isDuplicate = true;
        } else {
          // Check for near-duplicates (same content within 1 minute - 60000ms)
          for (const existingMsg of uniqueMessages) {
            const existingContent = (existingMsg.text || existingMsg.content || '').trim();
            const existingTime = new Date(existingMsg.timestamp || existingMsg.created_at).getTime();
            
            if (existingContent === content && Math.abs(currentTime - existingTime) < 60000) {
              isDuplicate = true;
              break;
            }
          }
        }
        
        if (!isDuplicate) {
          seenMessages.add(normalizedKey);
          uniqueMessages.push(current);
        }
      });
      
      // Replace messages with deduplicated ones and sort by timestamp
      conversation.messages = uniqueMessages.sort((a, b) => new Date(a.timestamp || a.created_at) - new Date(b.timestamp || b.created_at));
      
      // Recalculate validReplyCount and autoReplyCount AFTER deduplication
      // This ensures counts match exactly what the Valid tab shows
      conversation.validReplyCount = 0;
      conversation.autoReplyCount = 0;
      conversation.hasValidReplies = false;
      
      conversation.messages.forEach(msg => {
        if (msg.type === 'received' && msg.validation_status === 'valid') {
          conversation.hasValidReplies = true;
          conversation.validReplyCount += 1;
        } else if (msg.type === 'received' && (msg.validation_status === 'auto_reply' || msg.validation_status === 'out_of_office')) {
          conversation.autoReplyCount += 1;
        }
      });
    });
    
    return Array.from(conversationMap.values())
      .sort((a, b) => new Date(b.lastMessageDate) - new Date(a.lastMessageDate));
  }, [leads, leadEvents, messages]);

  // Update conversations state when processed conversations change
   useEffect(() => {
     setConversations(processedConversations);
   }, [processedConversations]);

  // Filter conversations
  const filteredConversations = useMemo(() => {
    let filtered = conversations;
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(conv => 
        conv.leadEmail.toLowerCase().includes(search) ||
        conv.leadName.toLowerCase().includes(search) ||
        conv.leadCompany.toLowerCase().includes(search) ||
        conv.campaignName.toLowerCase().includes(search)
      );
    }
    
    if (filterStatus === 'unread') {
      filtered = filtered.filter(conv => conv.unreadCount > 0);
    } else if (filterStatus === 'read') {
      filtered = filtered.filter(conv => conv.unreadCount === 0);
    } else if (filterStatus === 'valid') {
      // Valid tab: Only show conversations with AI-validated genuine replies
      // Conversations are grouped by lead just like other tabs
      filtered = filtered.filter(conv => conv.hasValidReplies && conv.validReplyCount > 0);
    }
    
    return filtered;
  }, [conversations, searchTerm, filterStatus]);

// ...
  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedConversation) return;
    
    // Store the message to send
    const messageToSend = replyText;
    
    try {
      // Clear the reply text immediately
      setReplyText('');
      
      // Send the actual email reply - message will appear when saved to database
      await sendEmailReply(selectedConversation.leadEmail, messageToSend);
      
      logger.log('Email reply sent successfully');
    } catch (error) {
      logger.error('Error sending email reply:', error);
      // Restore the text if sending failed
      setReplyText(messageToSend);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'interested': 'bg-green-100 text-green-800',
      'call_list': 'bg-orange-100 text-orange-800',
      'archived': 'bg-gray-100 text-gray-800',
      'converted': 'bg-yellow-100 text-yellow-800',
      'unqualified': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const ConversationList = () => (
    <div className="w-1/3 border-r border-gray-200 bg-white flex flex-col">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-100 bg-white">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Messages</h2>
        
        {/* AI Validation Status Info */}
        <div className="flex items-center space-x-1.5 text-xs text-gray-400 mb-3">
          <Sparkles className="w-3 h-3" />
          <span>Valid replies verified by AI • Results may occasionally vary</span>
        </div>
        
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 focus:bg-white transition-all duration-200 text-sm"
          />
        </div>
        
        {/* Filter Tabs */}
        <div className="flex space-x-1 bg-gray-50 rounded-xl p-1">
          {[
            { key: 'unread', label: 'Unread', count: conversations.filter(c => c.unreadCount > 0).length, isUnread: true },
            { key: 'read', label: 'Read', count: conversations.filter(c => c.unreadCount === 0).length },
            { key: 'valid', label: 'Valid', count: conversations.filter(c => c.hasValidReplies && c.validReplyCount > 0).length, isValid: true }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilterStatus(tab.key)}
              className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                filterStatus === tab.key
                  ? tab.isValid ? 'bg-green-500 text-white shadow-sm' 
                    : tab.isUnread ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white text-blue-600 shadow-sm'
                  : tab.isValid ? 'text-green-600 hover:text-green-700 hover:bg-green-50' 
                    : tab.isUnread ? 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {tab.isValid && <CheckCircle className="w-3 h-3 inline mr-1" />}
              {tab.isUnread && <Mail className="w-3 h-3 inline mr-1" />}
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>
      
      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {filteredConversations.map((conversation) => {
          // Get the last message's validation status
          const lastMessage = conversation.messages[conversation.messages.length - 1];
          const lastMessageValidation = lastMessage?.validation_status;
          
          return (
            <motion.div
              key={conversation.leadEmail}
              whileHover={{ scale: 1.005 }}
              onClick={() => handleSelectConversation(conversation)}
              className={`px-4 py-3 border-b border-gray-100 cursor-pointer transition-all duration-200 ${
                selectedConversation?.leadEmail === conversation.leadEmail
                  ? 'bg-blue-50 border-l-4 border-l-blue-500'
                  : conversation.unreadCount > 0
                    ? filterStatus === 'valid' 
                      ? 'bg-gray-100 border-l-4 border-l-gray-500 hover:bg-gray-150' 
                      : 'bg-blue-25 border-l-4 border-l-blue-400 hover:bg-blue-50'
                    : 'hover:bg-gray-50 border-l-4 border-l-transparent'
              }`}
            >
              {/* Row 1: Profile + Email + Timestamp */}
              <div className="flex items-start space-x-3">
                {/* Profile Icon - Clickable */}
                <div 
                  onClick={(e) => handleNavigateToLead(conversation.leadEmail, e)}
                  className={`w-9 h-9 rounded-full flex items-center justify-center relative flex-shrink-0 cursor-pointer transition-all hover:ring-2 hover:ring-blue-300 ${
                    conversation.unreadCount > 0 ? 'bg-blue-500' : 'bg-gray-200'
                  }`}
                  title="View lead details"
                >
                  <User className={`w-4 h-4 ${
                    conversation.unreadCount > 0 ? 'text-white' : 'text-gray-600'
                  }`} />
                  {conversation.unreadCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center ring-2 ring-white">
                      <span className="text-[10px] text-white font-bold">{conversation.unreadCount}</span>
                    </div>
                  )}
                </div>
                
                {/* Content Area */}
                <div className="flex-1 min-w-0">
                  {/* Email + Time Row */}
                  <div className="flex items-center justify-between gap-2">
                    <h3 
                      onClick={(e) => handleNavigateToLead(conversation.leadEmail, e)}
                      className={`text-sm truncate cursor-pointer hover:text-blue-600 transition-colors ${
                        conversation.unreadCount > 0 ? 'font-bold text-gray-900' : 'font-medium text-gray-700'
                      }`}
                      title="Click to view lead details"
                    >
                      {conversation.leadEmail}
                    </h3>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <span className="text-[10px] text-gray-400">
                        {conversation.lastMessageDate ? 
                          new Date(conversation.lastMessageDate).toLocaleDateString([], {month: 'short', day: 'numeric'}) : 
                          ''
                        }
                      </span>
                      {conversation.unreadCount === 0 && (
                        <CheckCheck className="w-3 h-3 text-green-500" title="Read" />
                      )}
                    </div>
                  </div>
                  
                  {/* Message Preview */}
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <p className={`text-xs truncate ${
                      conversation.unreadCount > 0 ? 'text-gray-800' : 'text-gray-500'
                    }`}>
                      {conversation.lastMessageText || 'No messages yet'}
                    </p>
                  </div>
                  
                  {/* Row 3: Campaign + Status Badges */}
                  <div className="flex items-center justify-between mt-1.5 gap-2">
                    <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded whitespace-nowrap overflow-hidden text-ellipsis max-w-[140px]" title={conversation.campaignName}>
                      {conversation.campaignName}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Show valid count if has valid replies */}
                      {conversation.validReplyCount > 0 && (
                        <span className="bg-green-500 text-white text-[10px] rounded-full px-1.5 py-0.5 font-medium flex items-center gap-0.5">
                          <CheckCircle className="w-2.5 h-2.5" />
                          {conversation.validReplyCount}
                        </span>
                      )}
                      {/* Show auto count only if no valid replies */}
                      {conversation.autoReplyCount > 0 && conversation.validReplyCount === 0 && (
                        <span className="bg-yellow-500 text-white text-[10px] rounded-full px-1.5 py-0.5 font-medium flex items-center gap-0.5">
                          <Bot className="w-2.5 h-2.5" />
                          {conversation.autoReplyCount}
                        </span>
                      )}
                      {/* Unread badge */}
                      {conversation.unreadCount > 0 && filterStatus !== 'valid' && (
                        <span className="bg-blue-600 text-white text-[10px] rounded-full px-1.5 py-0.5 font-medium">
                          new
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}

        {filteredConversations.length === 0 && (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-sm font-medium text-gray-900 mb-1">No conversations found</h3>
            <p className="text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search terms.' : 'Messages will appear here when leads reply to your campaigns.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const ConversationView = () => {
    if (!selectedConversation) {
      return (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Select a conversation</h3>
            <p className="text-gray-500">Choose a conversation from the list to view messages and reply.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 flex flex-col bg-white">
        {/* Conversation Header */}
        <div className="px-6 py-4 border-b border-gray-100 bg-white shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setSelectedConversation(null)}
                className="lg:hidden p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div 
                onClick={(e) => handleNavigateToLead(selectedConversation.leadEmail, e)}
                className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center shadow-sm cursor-pointer hover:from-blue-200 hover:to-blue-300 transition-all"
                title="View lead details"
              >
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <h3 
                    onClick={(e) => handleNavigateToLead(selectedConversation.leadEmail, e)}
                    className="text-sm font-bold text-gray-900 hover:text-blue-600 cursor-pointer truncate transition-colors"
                    title="Click to view lead details"
                  >
                    {selectedConversation.leadEmail}
                  </h3>
                  {/* Lead Status Badge */}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(selectedConversation.leadStatus)}`}>
                    {selectedConversation.leadStatus}
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1 flex-wrap">
                  <span className="flex items-center space-x-1 bg-blue-50 px-2 py-0.5 rounded-md">
                    <Target className="w-3 h-3" />
                    <span className="truncate max-w-[120px]">{selectedConversation.campaignName}</span>
                  </span>
                  <span className="bg-gray-100 px-2 py-0.5 rounded-md">
                    {filterStatus === 'valid' 
                      ? `${selectedConversation.messages.filter(m => m.type === 'sent' || m.validation_status === 'valid').length} valid`
                      : `${selectedConversation.messages.length} msgs`}
                  </span>
                  {selectedConversation.unreadCount === 0 ? (
                    <span className="flex items-center space-x-1 text-green-600 bg-green-50 px-2 py-0.5 rounded-md">
                      <CheckCheck className="w-3 h-3" />
                      <span>Read</span>
                    </span>
                  ) : (
                    <span className="flex items-center space-x-1 text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md">
                      <span>{selectedConversation.unreadCount} unread</span>
                    </span>
                  )}
                  {/* Validation Status Summary */}
                  {selectedConversation.validReplyCount > 0 && (
                    <span className="flex items-center space-x-1 text-green-600 bg-green-50 px-2 py-0.5 rounded-md">
                      <CheckCircle className="w-3 h-3" />
                      <span>{selectedConversation.validReplyCount} valid</span>
                    </span>
                  )}
                  {selectedConversation.autoReplyCount > 0 && (
                    <span className="flex items-center space-x-1 text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded-md">
                      <Bot className="w-3 h-3" />
                      <span>{selectedConversation.autoReplyCount} auto</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <div className="flex items-center space-x-1 text-sm text-gray-600 bg-yellow-50 px-2 py-1 rounded-full">
                <Star className="w-3 h-3 text-yellow-500" />
                <span className="font-medium text-xs">{selectedConversation.leadScore}</span>
              </div>
              {/* Action Menu Button */}
              <div className="relative" ref={actionMenuRef}>
                <button 
                  onClick={() => setShowActionMenu(!showActionMenu)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors hover:bg-gray-100"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
                
                {/* Action Menu Dropdown */}
                {showActionMenu && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <button
                      onClick={() => handleLeadStatusChange('interested')}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-green-50 flex items-center space-x-2"
                    >
                      <ThumbsUp className="w-4 h-4 text-green-500" />
                      <span>Mark as Interested</span>
                    </button>
                    <button
                      onClick={() => handleLeadStatusChange('archived')}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-red-50 flex items-center space-x-2"
                    >
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span>Mark as Canceled</span>
                    </button>
                    <button
                      onClick={() => handleLeadStatusChange('converted')}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-yellow-50 flex items-center space-x-2"
                    >
                      <Trophy className="w-4 h-4 text-yellow-500" />
                      <span>Deal Done</span>
                    </button>
                    <div className="border-t border-gray-100 my-1"></div>
                    {automationStatus.hasActiveAutomation ? (
                      <button
                        onClick={handleStopAutomation}
                        disabled={isStoppingAutomation}
                        className="w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50 flex items-center space-x-2"
                      >
                        <StopCircle className="w-4 h-4 text-red-500" />
                        <span>{isStoppingAutomation ? 'Stopping...' : `Stop Automation (${automationStatus.activeSequences.length})`}</span>
                      </button>
                    ) : (
                      <div className="px-4 py-2 text-sm text-gray-400 flex items-center space-x-2">
                        <Zap className="w-4 h-4 text-gray-300" />
                        <span>No active automation</span>
                      </div>
                    )}
                    <div className="border-t border-gray-100 my-1"></div>
                    <button
                      onClick={(e) => {
                        handleNavigateToLead(selectedConversation.leadEmail, e);
                        setShowActionMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 flex items-center space-x-2"
                    >
                      <ExternalLink className="w-4 h-4 text-blue-500" />
                      <span>View Lead Details</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
          {(filterStatus === 'valid' 
            ? selectedConversation.messages.filter(msg => 
                msg.type === 'sent' || msg.validation_status === 'valid'
              )
            : selectedConversation.messages
          ).map((message, index) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`flex ${message.type === 'sent' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-xs lg:max-md px-3 py-2 rounded-2xl shadow-sm relative ${
                message.type === 'sent'
                  ? message.email_status === 'pending' 
                    ? 'bg-yellow-500 text-white rounded-br-md'
                    : message.email_status === 'failed'
                    ? 'bg-red-500 text-white rounded-br-md'
                    : 'bg-blue-500 text-white rounded-br-md'
                  : message.validation_status === 'valid'
                    ? 'bg-white text-gray-900 rounded-bl-md border-2 border-green-400'
                    : message.validation_status === 'auto_reply' || message.validation_status === 'out_of_office'
                    ? 'bg-yellow-50 text-gray-900 rounded-bl-md border-2 border-yellow-400'
                    : 'bg-white text-gray-900 rounded-bl-md border border-gray-200'
              }`}>
                {/* Validation Status Indicator for received messages */}
                {message.type === 'received' && message.validation_status && (
                  <div className={`absolute -top-2 -left-2 w-5 h-5 rounded-full flex items-center justify-center ${
                    message.validation_status === 'valid' 
                      ? 'bg-green-500' 
                      : message.validation_status === 'auto_reply' || message.validation_status === 'out_of_office'
                      ? 'bg-yellow-500'
                      : 'bg-gray-400'
                  }`}>
                    {message.validation_status === 'valid' ? (
                      <CheckCircle className="w-3 h-3 text-white" />
                    ) : message.validation_status === 'auto_reply' || message.validation_status === 'out_of_office' ? (
                      <Bot className="w-3 h-3 text-white" />
                    ) : (
                      <AlertCircle className="w-3 h-3 text-white" />
                    )}
                  </div>
                )}
                <p className="text-sm leading-relaxed">{message.text}</p>
                {message.error_message && message.email_status === 'failed' && (
                  <p className="text-xs mt-1 text-red-100 bg-red-600 bg-opacity-50 px-2 py-1 rounded">
                    Error: {message.error_message}
                  </p>
                )}
                {/* Validation Status Label for received messages */}
                {message.type === 'received' && message.validation_status && message.validation_status !== 'pending' && (
                  <div className={`text-xs mt-1 px-2 py-0.5 rounded-full inline-block ${
                    message.validation_status === 'valid'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {message.validation_status === 'valid' ? 'Valid Reply' : 
                     message.validation_status === 'auto_reply' ? 'Auto Reply' : 'Out of Office'}
                  </div>
                )}
                <div className={`flex items-center justify-end mt-1 ${
                   message.type === 'sent' ? 'text-white text-opacity-70' : 'text-gray-400'
                 }`}>
                   <span className="text-xs">
                     {new Date(message.timestamp).toLocaleDateString([], {month: 'short', day: 'numeric'})} {new Date(message.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                   </span>
                   {message.type === 'sent' && (
                     <div className="flex items-center space-x-1 ml-2">
                       <div className={`w-2 h-2 rounded-full ${
                         message.email_status === 'pending' ? 'bg-yellow-300' :
                         message.email_status === 'failed' ? 'bg-red-300' :
                         'bg-green-300'
                       }`}></div>
                       <span className="text-xs">
                         {message.email_status === 'pending' ? 'Pending' :
                          message.email_status === 'failed' ? 'Failed' :
                          'Delivered'}
                       </span>
                     </div>
                   )}
                 </div>
              </div>
            </motion.div>
          ))}
          
          {/* Add some padding at the bottom */}
          <div className="h-4"></div>
        </div>
        
        {/* Enhanced Reply Input */}
        <div className="px-6 py-4 border-t border-blue-100 bg-blue-50">
          {/* Message Type Toggle */}
          <div className="flex items-center space-x-2 mb-3">
            <div className="flex bg-white rounded-lg p-1 border border-blue-200">
              <button
                onClick={() => setMessageType('message')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-all duration-200 ${
                  messageType === 'message'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <MessageSquare className="w-3 h-3 inline mr-1" />
                Internal Message
              </button>
              <button
                onClick={() => setMessageType('email')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-all duration-200 ${
                  messageType === 'email'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Mail className="w-3 h-3 inline mr-1" />
                Email Reply
              </button>
            </div>
            {messageType === 'email' && (
              <div className="flex items-center space-x-1 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-md">
                <Mail className="w-3 h-3" />
                <span>Will send via email automation</span>
              </div>
            )}
          </div>
          
          <div className="flex items-end space-x-4">
            <button className="p-3 text-blue-500 hover:bg-blue-100 rounded-xl transition-all duration-200 group">
              <Paperclip className="w-5 h-5 group-hover:text-blue-700" />
            </button>
            <div className="flex-1 relative">
              <div className="bg-white border border-blue-200 rounded-2xl px-4 py-3 min-h-[48px] flex items-center focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all duration-200">
                <textarea
                  value={replyText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setReplyText(value);
                    // Auto-resize textarea
                    requestAnimationFrame(() => {
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                    });
                  }}
                  placeholder={messageType === 'email' ? 'Type your email reply...' : 'Type your internal message...'}
                  rows={1}
                  className="w-full bg-transparent border-none outline-none resize-none text-sm placeholder-gray-500 max-h-32"
                  style={{
                    minHeight: '22px',
                    height: 'auto'
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  autoComplete="off"
                />
              </div>
              {replyText.trim() && (
                <div className="absolute -bottom-6 right-3 text-xs text-blue-400">
                  Press Enter to send
                </div>
              )}
            </div>
            <motion.button
              whileHover={{ scale: replyText.trim() ? 1.05 : 1 }}
              whileTap={{ scale: replyText.trim() ? 0.95 : 1 }}
              onClick={handleSendMessage}
              disabled={!replyText.trim() || isSending}
              className={`p-3 rounded-xl transition-all duration-200 flex-shrink-0 flex items-center justify-center ${
                replyText.trim() && !isSending
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isSending ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </motion.button>
          </div>
          <div className="flex items-center justify-between mt-3 text-xs text-blue-600">
            <span className="flex items-center space-x-2">
              <span>Shift + Enter for new line</span>
              <span>•</span>
              <span>{messageType === 'email' ? 'Email will be sent via automation' : 'Internal message only'}</span>
            </span>
            <span className={`px-2 py-1 rounded-full ${
              replyText.length > 800 ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
            }`}>
              {replyText.length}/1000
            </span>
          </div>
        </div>
      </div>
    );
  };

  const RightPanel = () => {
    if (!selectedConversation) return null;
    
    return (
      <div className={`${rightPanelCollapsed ? 'w-12' : 'w-80'} border-l border-gray-200 bg-white flex flex-col transition-all duration-300`}>
        {/* Panel Header */}
        <div className="px-4 py-3 border-b border-gray-100 bg-blue-50">
          <div className="flex items-center justify-between">
            {!rightPanelCollapsed && (
              <h3 className="text-sm font-semibold text-blue-900">Lead Details</h3>
            )}
            <button
              onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
              className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
            >
              {rightPanelCollapsed ? <ExternalLink className="w-4 h-4" /> : <X className="w-4 h-4" />}
            </button>
          </div>
        </div>
        
        {!rightPanelCollapsed && (
          <>
            {/* View Tabs */}
            <div className="px-4 py-2 border-b border-gray-100">
              <div className="flex space-x-1 bg-gray-50 rounded-lg p-1">
                {rightPanelViews.map(view => {
                  const Icon = view.icon;
                  return (
                    <button
                      key={view.id}
                      onClick={() => setRightPanelView(view.id)}
                      className={`flex-1 px-2 py-1 text-xs font-medium rounded-md transition-all duration-200 flex items-center justify-center space-x-1 ${
                        rightPanelView === view.id
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      <span className="hidden sm:inline">{view.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {rightPanelView === 'timeline' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-900">Activity Timeline</h4>
                    <Activity className="w-4 h-4 text-blue-600" />
                  </div>
                  {timelineEvents.length > 0 ? (
                    <div className="space-y-2">
                      {timelineEvents.slice(0, 10).map(event => {
                        const Icon = event.icon;
                        return (
                          <div key={event.id} className="flex items-start space-x-2 p-2 bg-gray-50 rounded-lg">
                            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <Icon className="w-3 h-3 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-900">{event.title}</p>
                              <p className="text-xs text-gray-600 truncate">{event.description}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {formatTimeInUserTimezone(event.timestamp)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 text-center py-4">No activity yet</p>
                  )}
                </div>
              )}
              
              {rightPanelView === 'notes' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900">Notes</h4>
                    <button
                      onClick={() => setShowNoteInput(!showNoteInput)}
                      className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {showNoteInput && (
                    <div className="space-y-2">
                      <textarea
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder="Add a note..."
                        className="w-full p-2 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={3}
                      />
                      <div className="flex space-x-2">
                        <button
                          onClick={handleAddNote}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setShowNoteInput(false);
                            setNewNote('');
                          }}
                          className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded-md hover:bg-gray-300 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    {leadNotes.map(note => (
                      <div key={note.id} className="p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-xs text-gray-900">{note.content}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatTimeInUserTimezone(note.created_at)}
                        </p>
                      </div>
                    ))}
                    {leadNotes.length === 0 && (
                      <p className="text-xs text-gray-500 text-center py-4">No notes yet</p>
                    )}
                  </div>
                </div>
              )}
              
              {rightPanelView === 'tasks' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900">Tasks</h4>
                    <button
                      onClick={() => setShowTaskInput(!showTaskInput)}
                      className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {showTaskInput && (
                    <div className="space-y-2">
                      <input
                        value={newTask}
                        onChange={(e) => setNewTask(e.target.value)}
                        placeholder="Add a task..."
                        className="w-full p-2 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <div className="flex space-x-2">
                        <button
                          onClick={handleAddTask}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Add
                        </button>
                        <button
                          onClick={() => {
                            setShowTaskInput(false);
                            setNewTask('');
                          }}
                          className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded-md hover:bg-gray-300 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    {leadTasks.map(task => (
                      <div key={task.id} className="flex items-start space-x-2 p-2 bg-gray-50 rounded-lg">
                        <button
                          onClick={() => handleToggleTask(task.id)}
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center mt-0.5 ${
                            task.completed
                              ? 'bg-green-500 border-green-500 text-white'
                              : 'border-gray-300 hover:border-blue-500'
                          }`}
                        >
                          {task.completed && <CheckSquare className="w-3 h-3" />}
                        </button>
                        <div className="flex-1">
                          <p className={`text-xs ${
                            task.completed ? 'text-gray-500 line-through' : 'text-gray-900'
                          }`}>
                            {task.title}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTimeInUserTimezone(task.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                    {leadTasks.length === 0 && (
                      <p className="text-xs text-gray-500 text-center py-4">No tasks yet</p>
                    )}
                  </div>
                </div>
              )}
              
              {rightPanelView === 'calendar' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900">Meetings</h4>
                    <button
                      onClick={() => setShowMeetingForm(!showMeetingForm)}
                      className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {showMeetingForm && (
                    <div className="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <input
                        value={meetingForm.title}
                        onChange={(e) => setMeetingForm({...meetingForm, title: e.target.value})}
                        placeholder="Meeting title"
                        className="w-full p-2 text-xs border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <textarea
                        value={meetingForm.description}
                        onChange={(e) => setMeetingForm({...meetingForm, description: e.target.value})}
                        placeholder="Description (optional)"
                        className="w-full p-2 text-xs border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={2}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="date"
                          value={meetingForm.date}
                          onChange={(e) => setMeetingForm({...meetingForm, date: e.target.value})}
                          className="p-2 text-xs border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <input
                          type="time"
                          value={meetingForm.time}
                          onChange={(e) => setMeetingForm({...meetingForm, time: e.target.value})}
                          className="p-2 text-xs border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleScheduleMeeting}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Schedule
                        </button>
                        <button
                          onClick={() => setShowMeetingForm(false)}
                          className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded-md hover:bg-gray-300 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    {leadMeetings.map(meeting => (
                      <div key={meeting.id} className="p-2 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs font-medium text-gray-900">{meeting.title}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          {formatDateInUserTimezone(meeting.meeting_date)} at {meeting.meeting_time}
                        </p>
                        {meeting.description && (
                          <p className="text-xs text-gray-500 mt-1">{meeting.description}</p>
                        )}
                      </div>
                    ))}
                    {leadMeetings.length === 0 && (
                      <p className="text-xs text-gray-500 text-center py-4">No meetings scheduled</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Inbox</h1>
              <p className="text-sm text-gray-500">Manage your inbox efficiently</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowSearch(!showSearch)}
              className={`p-2 rounded-lg transition-all duration-200 ${
                showSearch ? 'bg-blue-100 text-blue-600 shadow-sm' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <Search className="w-5 h-5" />
            </button>
            <div className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {filteredConversations.length} conversations
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <AnimatePresence>
          {showSearch && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex gap-4 mt-4 pt-4 border-t border-gray-100"
            >
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 focus:bg-white transition-all duration-200"
                  autoFocus
                />
              </div>
              <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 focus:bg-white transition-all duration-200"
                >
                  <option value="all">All Conversations</option>
                  <option value="unread">Unread Only</option>
                  <option value="read">Read Only</option>
                </select>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        <ConversationList />
        <ConversationView />
      </div>
    </div>
  );
};

export default Inbox;